import { useQuery } from '@tanstack/react-query'
import { conversationAPI } from '@/services/api'
import { Conversation, normalizeConversationDates } from '@/stores/conversationStore'

/**
 * Query-key factory for the conversation list + derived counts.
 * Keyed by team so switching teams is a distinct cache entry (no manual refetch),
 * and shared across every consumer so concurrent mounts dedupe to one request.
 */
export const conversationKeys = {
  all: ['conversations'] as const,
  list: (teamId: string | null) => ['conversations', 'list', teamId] as const,
  count: (teamId: string | null, collectionTypes: string[]) =>
    ['conversations', 'count', teamId, [...collectionTypes].sort().join(',')] as const,
}

/**
 * Server default page size for the conversation list (conversationController).
 * When the store holds fewer than this, it has every conversation, so a filtered
 * count can be derived client-side and the count query is unnecessary.
 */
export const STORE_PAGE_LIMIT = 200

export interface ConversationsResult {
  conversations: Conversation[]
  pagination?: { total: number; limit: number; offset: number; hasMore: boolean }
}

/**
 * The single source of truth for the full conversation list (all collection types).
 * Replaces the per-mount fetch in App.tsx — react-query dedupes concurrent callers
 * and serves cached data on revisit within staleTime.
 */
export function useConversations(teamId: string | null, enabled: boolean) {
  return useQuery({
    queryKey: conversationKeys.list(teamId),
    enabled,
    queryFn: async (): Promise<ConversationsResult> => {
      const res = teamId
        ? await conversationAPI.getByTeam(teamId)
        : await conversationAPI.getAll()
      return {
        conversations: res.conversations.map(normalizeConversationDates),
        pagination: res.pagination,
      }
    },
  })
}

/**
 * Server-side total for a filtered slice (e.g. VIDEO/SLIDE), needed because the
 * list query is capped (default limit 200) so client-side counting is wrong for
 * power users. Shared key means HeroScreen and the Videos page collapse to one call.
 */
export function useConversationsCount(
  teamId: string | null,
  collectionTypes: string[],
  enabled: boolean,
) {
  return useQuery({
    queryKey: conversationKeys.count(teamId, collectionTypes),
    enabled,
    queryFn: async (): Promise<number> => {
      const res = teamId
        ? await conversationAPI.getByTeam(teamId, { limit: 1, offset: 0, collectionTypes })
        : await conversationAPI.getAll({ limit: 1, offset: 0, collectionTypes })
      return res.pagination?.total ?? 0
    },
  })
}
